import React, { useEffect, useState } from 'react';
import { fetchPreferences, savePreferences } from '../../../lib/preferences';

import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const Preferences = () => {
  const [preferences, setPreferences] = useState({
    emailNotifications: {},
    smsNotifications: {},
    privacy: {},
    account: {},
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchPreferences().then((res) => {
      if (cancelled) return;
      if (res.error) { setLoadError(res.error); return; }
      setPreferences(res.preferences);
    });
    return () => { cancelled = true; };
  }, []);

  const handleTogglePreference = (category, key) => {
    setPreferences(prev => ({
      ...prev,
      [category]: {
        ...prev?.[category],
        [key]: !prev?.[category]?.[key]
      }
    }));
    setHasChanges(true);
  };

  const handleSelectPreference = (category, key, value) => {
    setPreferences(prev => ({
      ...prev,
      [category]: {
        ...prev?.[category],
        [key]: value
      }
    }));
    setHasChanges(true);
  };

  const handleSavePreferences = async () => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    const res = await savePreferences(preferences);
    setSaving(false);
    if (res.error) { setSaveError(res.error); return; }
    setPreferences(res.preferences);
    setHasChanges(false);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2500);
  };

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Español' },
    { code: 'fr', name: 'Français' },
    { code: 'de', name: 'Deutsch' }
  ];

  const currencies = [
    { code: 'USD', name: 'US Dollar ($)', symbol: '$' },
    { code: 'EUR', name: 'Euro (€)', symbol: '€' },
    { code: 'GBP', name: 'British Pound (£)', symbol: '£' },
    { code: 'CAD', name: 'Canadian Dollar (C$)', symbol: 'C$' }
  ];

  const timezones = [
    { id: 'America/New_York', name: 'Eastern Time (ET)' },
    { id: 'America/Chicago', name: 'Central Time (CT)' },
    { id: 'America/Denver', name: 'Mountain Time (MT)' },
    { id: 'America/Los_Angeles', name: 'Pacific Time (PT)' }
  ];

  return (
    <div className="max-w-4xl">
      <div className="bg-white rounded-lg border border-border overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                Preferences
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Manage your notification preferences and account settings
              </p>
            </div>
            {hasChanges && (
              <Button
                onClick={handleSavePreferences}
                iconName="Check"
                iconPosition="left"
              >
                Save Changes
              </Button>
            )}
          </div>
        </div>

        <div className="divide-y divide-border">
          {/* Email Notifications */}
          <div className="p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Icon name="Mail" size={20} className="text-primary" />
              <div>
                <h3 className="text-lg font-medium text-foreground">
                  Email Notifications
                </h3>
                <p className="text-sm text-muted-foreground">
                  Choose which emails you'd like to receive
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="font-medium text-foreground">Order Updates</p>
                  <p className="text-sm text-muted-foreground">
                    Shipping confirmations, delivery notifications, and order status changes
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={preferences?.emailNotifications?.orderUpdates}
                    onChange={() => handleTogglePreference('emailNotifications', 'orderUpdates')}
                    className="sr-only peer" 
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>

              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="font-medium text-foreground">Newsletter</p>
                  <p className="text-sm text-muted-foreground">
                    Weekly style tips, product launches, and exclusive content
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={preferences?.emailNotifications?.newsletter}
                    onChange={() => handleTogglePreference('emailNotifications', 'newsletter')}
                    className="sr-only peer" 
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>

              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="font-medium text-foreground">Promotions & Sales</p>
                  <p className="text-sm text-muted-foreground">
                    Special offers, discounts, and seasonal sales
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={preferences?.emailNotifications?.promotions}
                    onChange={() => handleTogglePreference('emailNotifications', 'promotions')}
                    className="sr-only peer" 
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>

              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="font-medium text-foreground">Restock Alerts</p>
                  <p className="text-sm text-muted-foreground">
                    Notifications when out-of-stock items become available
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={preferences?.emailNotifications?.restockAlerts}
                    onChange={() => handleTogglePreference('emailNotifications', 'restockAlerts')}
                    className="sr-only peer" 
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
            </div>
          </div>

          {/* SMS Notifications */}
          <div className="p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Icon name="MessageSquare" size={20} className="text-primary" />
              <div>
                <h3 className="text-lg font-medium text-foreground">
                  SMS Notifications
                </h3>
                <p className="text-sm text-muted-foreground">
                  Receive important updates via text message
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="font-medium text-foreground">Delivery Updates</p>
                  <p className="text-sm text-muted-foreground">
                    Real-time delivery notifications and driver updates
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={preferences?.smsNotifications?.deliveryUpdates}
                    onChange={() => handleTogglePreference('smsNotifications', 'deliveryUpdates')}
                    className="sr-only peer" 
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
            </div>
          </div>

          {/* Privacy Settings */}
          <div className="p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Icon name="Shield" size={20} className="text-primary" />
              <div>
                <h3 className="text-lg font-medium text-foreground">
                  Privacy Settings
                </h3>
                <p className="text-sm text-muted-foreground">
                  Control your privacy and data sharing preferences
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="font-medium text-foreground">Profile Visibility</p>
                  <p className="text-sm text-muted-foreground">
                    Control who can see your profile information
                  </p>
                </div>
                <select
                  value={preferences?.privacy?.profileVisibility}
                  onChange={(e) => handleSelectPreference('privacy', 'profileVisibility', e?.target?.value)}
                  className="px-3 py-2 border border-input rounded-md text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
                >
                  <option value="private">Private</option>
                  <option value="friends">Friends Only</option>
                  <option value="public">Public</option>
                </select>
              </div>

              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="font-medium text-foreground">Personalized Ads</p>
                  <p className="text-sm text-muted-foreground">
                    Show ads based on your interests and shopping behavior
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={preferences?.privacy?.personalizedAds}
                    onChange={() => handleTogglePreference('privacy', 'personalizedAds')}
                    className="sr-only peer" 
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
            </div>
          </div>

          {/* Account Settings */}
          <div className="p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Icon name="Settings" size={20} className="text-primary" />
              <div>
                <h3 className="text-lg font-medium text-foreground">
                  Account Settings
                </h3>
                <p className="text-sm text-muted-foreground">
                  Language, currency, and regional preferences
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Language
                </label>
                <select
                  value={preferences?.account?.language}
                  onChange={(e) => handleSelectPreference('account', 'language', e?.target?.value)}
                  className="w-full px-3 py-2 border border-input rounded-md text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
                >
                  {languages?.map(lang => (
                    <option key={lang?.code} value={lang?.code}>
                      {lang?.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Currency
                </label>
                <select
                  value={preferences?.account?.currency}
                  onChange={(e) => handleSelectPreference('account', 'currency', e?.target?.value)}
                  className="w-full px-3 py-2 border border-input rounded-md text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
                >
                  {currencies?.map(currency => (
                    <option key={currency?.code} value={currency?.code}>
                      {currency?.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Timezone
                </label>
                <select
                  value={preferences?.account?.timezone}
                  onChange={(e) => handleSelectPreference('account', 'timezone', e?.target?.value)}
                  className="w-full px-3 py-2 border border-input rounded-md text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
                >
                  {timezones?.map(tz => (
                    <option key={tz?.id} value={tz?.id}>
                      {tz?.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Measurement Unit
                </label>
                <select
                  value={preferences?.account?.measurementUnit}
                  onChange={(e) => handleSelectPreference('account', 'measurementUnit', e?.target?.value)}
                  className="w-full px-3 py-2 border border-input rounded-md text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
                >
                  <option value="imperial">Imperial (inches, lbs)</option>
                  <option value="metric">Metric (cm, kg)</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Preferences;